import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Palette, Copy, Trash2, PlusCircle, Save, Image as ImageIcon, Loader2 } from "lucide-react";

const BLANK = {
  name: "",
  tagline: "",
  heading_font: "Fraunces",
  body_font: "Manrope",
  primary_color: "#1A1A1A",
  accent_color: "#CBA135",
  background_color: "#F9F9F8",
  page_size: "A4",
  default_layout: "editorial",
  show_price_default: true,
  default_price_rule: { mode: "original", type: "percent", value: 0 },
  logo_file_id: null,
};

export default function PresetsPage() {
  const [presets, setPresets] = useState([]);
  const [editing, setEditing] = useState(null); // preset object or BLANK for new
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);
  const [config, setConfig] = useState(null);

  const refresh = () => api.get("/presets").then((r) => setPresets(r.data));

  useEffect(() => {
    refresh();
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const startNew = () => setEditing({ ...BLANK });
  const startEdit = (p) => setEditing({ ...p });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing?.name?.trim()) {
      toast.error("Preset name is required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/presets/${editing.id}`, editing);
        toast.success("Preset updated");
      } else {
        await api.post("/presets", editing);
        toast.success("Preset created");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/presets/${id}/duplicate`);
      toast.success("Preset duplicated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this preset? This cannot be undone.")) return;
    try {
      await api.delete(`/presets/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onLogoChosen = async (file) => {
    if (!file || !editing) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "logos");
    setUploadingLogo(true);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, logo_file_id: data.id }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error("Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8" data-testid="presets-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <div className="overline">Branding</div>
          <h1 className="font-serif-editorial text-4xl mt-2">Presets Library</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Shared across the team. Any user can load, edit, or delete.
          </p>
        </div>
        <button
          data-testid="new-preset-btn"
          onClick={startNew}
          className="flex items-center gap-2 bg-neutral-900 text-white text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-black transition-all"
        >
          <PlusCircle className="w-3.5 h-3.5" /> New Preset
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {presets.length === 0 && (
          <div className="col-span-full text-center py-16 border hairline bg-white rounded-sm">
            <Palette className="w-6 h-6 mx-auto text-neutral-400 mb-3" />
            <p className="text-sm text-neutral-500">No presets yet. Create your first shared branding preset.</p>
          </div>
        )}
        {presets.map((p) => (
          <div key={p.id} data-testid={`preset-card-${p.id}`} className="paper rounded-sm p-6 hover-lift">
            <div className="flex items-start justify-between gap-2 mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-serif-editorial text-2xl leading-tight truncate">{p.name}</h3>
                {p.tagline && <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{p.tagline}</div>}
              </div>
              {p.logo_file_id && (
                <img src={fileUrl(p.logo_file_id)} alt="" className="h-8 max-w-20 object-contain" />
              )}
            </div>
            <div className="flex gap-2 mb-4">
              <ColorDot color={p.primary_color} label="Primary" />
              <ColorDot color={p.accent_color} label="Accent" />
              <ColorDot color={p.background_color} label="BG" />
            </div>
            <div className="text-[11px] text-neutral-500 tracking-widest uppercase mb-4">
              {p.heading_font} · {p.body_font} · {p.page_size} · {p.default_layout || "editorial"}
            </div>
            <div className="flex gap-1.5">
              <button
                data-testid={`edit-preset-${p.id}`}
                onClick={() => startEdit(p)}
                className="flex-1 text-[10px] uppercase tracking-widest px-3 py-2 bg-neutral-900 text-white rounded-sm hover:bg-black"
              >
                Edit
              </button>
              <button
                data-testid={`duplicate-preset-${p.id}`}
                onClick={() => duplicate(p.id)}
                className="p-2 border hairline rounded-sm hover:bg-neutral-50"
                title="Duplicate"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`delete-preset-${p.id}`}
                onClick={() => remove(p.id)}
                className="p-2 border hairline rounded-sm hover:bg-neutral-50 hover:text-red-600"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Editor modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
          data-testid="preset-editor"
        >
          <div className="paper rounded-t-xl sm:rounded-sm max-w-2xl w-full max-h-[92vh] overflow-auto p-5 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif-editorial text-2xl">
                {editing.id ? "Edit Preset" : "New Preset"}
              </h2>
              <button
                data-testid="close-editor-btn"
                onClick={cancel}
                className="text-xs uppercase tracking-widest text-neutral-500"
              >
                Cancel
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Name">
                <input
                  data-testid="preset-name-input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="input"
                />
              </F>
              <F label="Tagline">
                <input
                  data-testid="preset-tagline-input"
                  value={editing.tagline || ""}
                  onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                  className="input"
                />
              </F>
              <F label="Heading Font">
                <select
                  data-testid="preset-heading-font"
                  value={editing.heading_font}
                  onChange={(e) => setEditing({ ...editing, heading_font: e.target.value })}
                  className="input"
                >
                  {(config?.fonts?.heading || ["Fraunces"]).map((f) => <option key={f}>{f}</option>)}
                </select>
              </F>
              <F label="Body Font">
                <select
                  data-testid="preset-body-font"
                  value={editing.body_font}
                  onChange={(e) => setEditing({ ...editing, body_font: e.target.value })}
                  className="input"
                >
                  {(config?.fonts?.body || ["Manrope"]).map((f) => <option key={f}>{f}</option>)}
                </select>
              </F>
              <F label="Primary">
                <input type="color" data-testid="preset-primary" value={editing.primary_color} onChange={(e) => setEditing({ ...editing, primary_color: e.target.value })} className="w-full h-10 rounded-sm border hairline bg-white" />
              </F>
              <F label="Accent">
                <input type="color" data-testid="preset-accent" value={editing.accent_color} onChange={(e) => setEditing({ ...editing, accent_color: e.target.value })} className="w-full h-10 rounded-sm border hairline bg-white" />
              </F>
              <F label="Background">
                <input type="color" data-testid="preset-background" value={editing.background_color} onChange={(e) => setEditing({ ...editing, background_color: e.target.value })} className="w-full h-10 rounded-sm border hairline bg-white" />
              </F>
              <F label="Page Size">
                <select data-testid="preset-page-size" value={editing.page_size} onChange={(e) => setEditing({ ...editing, page_size: e.target.value })} className="input">
                  {(config?.page_sizes || ["A4", "Letter", "A3", "A5"]).map((s) => <option key={s}>{s}</option>)}
                </select>
              </F>
              <F label="Default Layout">
                <select
                  data-testid="preset-default-layout"
                  value={editing.default_layout || "editorial"}
                  onChange={(e) => setEditing({ ...editing, default_layout: e.target.value })}
                  className="input"
                >
                  {(config?.layouts || [{ code: "editorial", label: "Editorial" }]).map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </F>
              <F label="Default price shown">
                <select
                  data-testid="preset-default-show-price"
                  value={editing.show_price_default ? "yes" : "no"}
                  onChange={(e) => setEditing({ ...editing, show_price_default: e.target.value === "yes" })}
                  className="input"
                >
                  <option value="yes">Show</option>
                  <option value="no">Hide</option>
                </select>
              </F>
              <F label="Default price rule">
                <select
                  data-testid="preset-default-rule-mode"
                  value={editing.default_price_rule?.mode || "original"}
                  onChange={(e) => setEditing({ ...editing, default_price_rule: { ...(editing.default_price_rule || {}), mode: e.target.value } })}
                  className="input"
                >
                  <option value="original">Original</option>
                  <option value="hide">Hide</option>
                  <option value="adjust">Adjust</option>
                </select>
              </F>
            </div>

            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Logo</div>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={logoRef}
                  className="hidden"
                  accept="image/*"
                  data-testid="preset-logo-input"
                  onChange={(e) => onLogoChosen(e.target.files?.[0])}
                />
                <button
                  data-testid="preset-upload-logo-btn"
                  onClick={() => logoRef.current?.click()}
                  className="text-xs uppercase tracking-widest px-3 py-2 border hairline rounded-sm bg-white hover:bg-neutral-50 flex items-center gap-2"
                >
                  {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                  {editing.logo_file_id ? "Replace" : "Upload"}
                </button>
                {editing.logo_file_id && <img src={fileUrl(editing.logo_file_id)} alt="" className="h-10" />}
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-2">
              <button
                data-testid="cancel-preset-btn"
                onClick={cancel}
                className="px-4 py-2 text-xs uppercase tracking-widest text-neutral-600 hover:text-black"
              >
                Cancel
              </button>
              <button
                data-testid="save-preset-btn"
                onClick={save}
                disabled={saving}
                className="flex items-center gap-2 bg-neutral-900 text-white text-xs uppercase tracking-widest px-4 py-3 sm:py-2 min-h-[44px] sm:min-h-0 rounded-sm hover:bg-black disabled:opacity-40"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input {
          width: 100%;
          border: 1px solid var(--brand-border);
          border-radius: 2px;
          background: #fff;
          font-size: 13px;
          padding: 8px 12px;
        }
        .input:focus { outline: none; border-color: #1A1A1A; }
      `}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-neutral-500 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ColorDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <span className="w-4 h-4 rounded-sm border hairline" style={{ background: color }} />
      <span className="text-[10px] uppercase tracking-widest text-neutral-500">{color}</span>
    </div>
  );
}
